import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchShipmentDetail,
  fetchShipmentActivity,
  fetchShipmentComments,
  postShipmentComment,
  fetchShipmentDocuments,
  uploadShipmentDocument,
  deleteShipmentDocument,
  deleteShipment,
  clearDetail,
} from '../../store/slices/shipmentTrackerSlice';
import imageCompression from 'browser-image-compression';
import { ShipmentDocumentCustomService } from '../../api/customServices/ShipmentDocumentCustomService';
import { addNotification } from '../../store/slices/notificationSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import ArrowBack from '../../components/ArrowBack';
import ShipmentStatusBadge from '../../components/shipment-tracker/ShipmentStatusBadge';
import ShipmentLabelList from '../../components/shipment-tracker/ShipmentLabelList';
import { useShipmentDateFormat } from '../../hooks/useShipmentDateFormat';
import ConfirmDeleteShipmentModal from '../../components/shipment-tracker/ConfirmDeleteShipmentModal';
import CostBreakdownTab from '../../components/shipment-tracker/CostBreakdownTab';
import ShipmentDocumentPreviewModal from '../../components/shipment-tracker/ShipmentDocumentPreviewModal';
import { isDocumentPreviewable } from '../../helpers/documentPreview';

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$' };

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString();
}

const COMMENTS_INITIAL_VISIBLE = 2;
const COMMENTS_LOAD_STEP = 5;

const MAX_DOCUMENT_SIZE = 5 * 1024 * 1024; // 5 MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatActivityEvent(
  eventType: string,
  params: Record<string, string> | null | undefined,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  switch (eventType) {
    case 'status_changed':
      return t('activity_status_changed', {
        from: params?.from ? t(`shipment_status_${params.from}`) : t('none'),
        to: params?.to ? t(`shipment_status_${params.to}`) : '-',
      });
    case 'item_added':
      return t('activity_item_added', { name: params?.productName ?? '' });
    case 'item_deleted':
      return t('activity_item_deleted', { name: params?.productName ?? '' });
    case 'item_edited':
      return t('activity_item_edited', { name: params?.productName ?? '' });
    case 'comment_posted':
      return t('activity_comment_posted');
    case 'shipment_created':
      return t('activity_shipment_created');
    case 'edited':
      return t('activity_shipment_edited');
    case 'reconciliation_updated':
      return t('activity_reconciliation_updated');
    case 'allocation_method_changed':
      return t('activity_allocation_method_changed');
    case 'cost_component_added':
      return t('activity_cost_component_added', { label: params?.label ?? '' });
    case 'cost_component_edited':
      return t('activity_cost_component_edited', {
        label: params?.label ?? '',
      });
    case 'cost_component_deleted':
      return t('activity_cost_component_deleted', {
        label: params?.label ?? '',
      });
    case 'document_attached':
      return t('activity_document_attached', { name: params?.fileName ?? '' });
    case 'document_deleted':
      return t('activity_document_deleted', { name: params?.fileName ?? '' });
    case 'archived':
      return t('activity_shipment_archived');
    case 'restored':
      return t('activity_shipment_restored');
    default:
      return eventType;
  }
}

const ShipmentTrackerDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const { canWrite, canViewFinancials } = useShipmentTrackerPermissions();
  const formatShipmentDate = useShipmentDateFormat();

  const { shipment, loading, error } = useAppSelector(
    (state) => state.shipmentTracker.detail,
  );
  const { items: activityItems, loading: activityLoading } = useAppSelector(
    (state) => state.shipmentTracker.activity,
  );
  const {
    items: commentItems,
    loading: commentsLoading,
    posting: commentPosting,
  } = useAppSelector((state) => state.shipmentTracker.comments);
  const {
    items: documentItems,
    loading: documentsLoading,
    uploading: documentUploading,
  } = useAppSelector((state) => state.shipmentTracker.documents);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{
    id: string;
    originalName: string;
    mimeType?: string | null;
  } | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [commentsVisible, setCommentsVisible] = useState(
    COMMENTS_INITIAL_VISIBLE,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shipmentId) {
      dispatch(fetchShipmentDetail(shipmentId));
      dispatch(fetchShipmentActivity(shipmentId));
      dispatch(fetchShipmentComments(shipmentId));
      dispatch(fetchShipmentDocuments(shipmentId));
    }
    return () => {
      dispatch(clearDetail());
    };
  }, [dispatch, shipmentId]);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const selected = input.files?.[0];
    if (!shipmentId || !selected) return;

    let file: File = selected;
    // Compress images client-side before upload.
    if (selected.type.startsWith('image/')) {
      try {
        const compressed = await imageCompression(selected, {
          maxSizeMB: 5,
          maxWidthOrHeight: 2560,
          useWebWorker: true,
        });
        // Compression can return a blob-like file named "blob" with no
        // extension; re-wrap it with the original name so the backend keeps
        // the correct extension/type.
        file = new File([compressed], selected.name, {
          type: compressed.type || selected.type,
        });
      } catch {
        file = selected;
      }
    }

    if (file.size > MAX_DOCUMENT_SIZE) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_document_too_big'),
        }),
      );
      input.value = '';
      return;
    }

    await dispatch(uploadShipmentDocument({ id: shipmentId, file }));
    input.value = '';
  };

  const handleDownloadDocument = (docId: string, originalName: string) => {
    if (!shipmentId) return;
    ShipmentDocumentCustomService.download(
      shipmentId,
      docId,
      originalName,
    ).catch(() => {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_document_download_failed'),
        }),
      );
    });
  };

  const handleDownloadAllDocuments = () => {
    if (!shipmentId) return;
    setDownloadingAll(true);
    ShipmentDocumentCustomService.downloadAll(shipmentId)
      .catch(() => {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message: t('shipment_document_download_failed'),
          }),
        );
      })
      .finally(() => setDownloadingAll(false));
  };

  const handleDeleteDocument = (docId: string) => {
    if (!shipmentId) return;
    dispatch(deleteShipmentDocument({ id: shipmentId, docId }));
  };

  const handlePostComment = async () => {
    const body = commentBody.trim();
    if (!shipmentId || !body) return;
    try {
      await dispatch(postShipmentComment({ id: shipmentId, body })).unwrap();
      setCommentBody('');
    } catch {
      // error notification fired in thunk
    }
  };

  const handleDelete = async () => {
    if (!shipmentId) return;
    setDeleting(true);
    try {
      await dispatch(deleteShipment(shipmentId)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('shipment_deleted'),
        }),
      );
      navigate('/features/shipment-tracker/shipments', { replace: true });
    } catch {
      // error shown in thunk
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) return <div className="page-loading">{t('loading')}</div>;
  if (error || !shipment)
    return <div className="page-error">{error ?? t('shipment_not_found')}</div>;

  const isDraft = shipment.workflowStatus === 'draft';
  const currencySymbol =
    CURRENCY_SYMBOL[shipment.currency] ?? shipment.currency;
  const isWriter = canWrite;
  const isFinancials = canViewFinancials;
  // Viewer: only Overview tab (no cost breakdown / reconciliation)
  const nameCol = isFinancials
    ? t('shipment_col_product')
    : t('shipment_col_variant');

  return (
    <div className="shipment-detail-page">
      {/* ── Header ── */}
      <div className="shipment-detail-header">
        <ArrowBack
          onClick={() => navigate('/features/shipment-tracker/shipments')}
        />
        <div className="shipment-detail-header__title-group">
          <h1>{shipment.name}</h1>
          <div className="shipment-detail-header__badges">
            <ShipmentStatusBadge
              status={shipment.executionStatus}
              isDraft={isDraft}
            />
            <ShipmentLabelList labels={shipment.labels ?? []} max={99} />
          </div>
        </div>
        {/* <div className="shipment-detail-header__meta">
          {shipment.reference && <span>{shipment.reference}</span>}
          {(shipment.originCountryCode || shipment.destinationCountryCode) && (
            <span>
              {[shipment.originCity, shipment.originCountryCode]
                .filter(Boolean)
                .join(", ")}
              {" → "}
              {[shipment.destinationCity, shipment.destinationCountryCode]
                .filter(Boolean)
                .join(", ")}
            </span>
          )}
          {shipment.eta && (
            <span>
              {t("shipment_col_eta")}: {shipment.eta}
            </span>
          )}
        </div> */}
        {isWriter && (
          <div className="shipment-detail-header__actions">
            <button
              className="btn btn-secondary"
              onClick={() =>
                navigate(
                  `/features/shipment-tracker/shipments/${shipmentId}/edit`,
                )
              }
            >
              {t('edit')}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              {t('delete')}
            </button>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="shipment-detail-body">
        {/* Plan summary */}
        <div className="detail-card">
          <h2 className="detail-card__title">
            {t('shipment_detail_plan_summary')}
          </h2>
          {(shipment.items ?? []).length === 0 ? (
            <p className="detail-card__empty">{t('shipment_items_empty')}</p>
          ) : (
            <table className="shipment-items-table">
              <thead>
                <tr>
                  <th>{nameCol}</th>
                  <th>{t('shipment_col_cartons')}</th>
                  <th>{t('shipment_col_units')}</th>
                  {isFinancials && (
                    <th>
                      {t('shipment_col_cost')} ({currencySymbol})
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(shipment.items ?? []).map((item) => (
                  <tr
                    key={item.id}
                    className={isWriter ? 'shipment-items-table__row' : ''}
                    onClick={
                      isWriter
                        ? () =>
                            navigate(
                              `/features/shipment-tracker/shipments/${shipmentId}/items/${item.id}/edit`,
                            )
                        : undefined
                    }
                    style={isWriter ? { cursor: 'pointer' } : undefined}
                  >
                    <td>
                      <div>{isFinancials ? item.productName : item.sku}</div>
                      <div className="text-muted">{item.sku}</div>
                      {/* The whole row is clickable for writers, but nothing said so — this is
                          the visible affordance. stopPropagation keeps it to one navigation. */}
                      {isWriter && (
                        <button
                          className="btn-link shipment-items-table__edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/features/shipment-tracker/shipments/${shipmentId}/items/${item.id}/edit`,
                            );
                          }}
                        >
                          {t('edit')}
                        </button>
                      )}
                    </td>
                    <td>{item.cartons ?? '-'}</td>
                    <td>{item.units ?? '-'}</td>
                    {isFinancials && (
                      <td>
                        {item.totalItemValue != null
                          ? `${currencySymbol}${item.totalItemValue}`
                          : '-'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Snapshot */}
        <div className="detail-card">
          <h2 className="detail-card__title">
            {t('shipment_detail_snapshot')}
          </h2>
          <dl className="detail-snapshot">
            {shipment.eta && (
              <>
                <dt>{t('shipment_col_eta')}</dt>
                <dd>{formatShipmentDate(shipment.eta)}</dd>
              </>
            )}
            {shipment.trackingUrl && (
              <>
                <dt>{t('shipment_field_tracking_url')}</dt>
                <dd>
                  <a
                    href={shipment.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={shipment.trackingUrl}
                  >
                    {shipment.trackingUrl}
                  </a>
                </dd>
              </>
            )}
            <dt>{t('shipment_field_transport_profile')}</dt>
            <dd>{shipment.transportProfileDisplay ?? '-'}</dd>
            <dt>{t('shipment_field_incoterm')}</dt>
            <dd>{shipment.incoterm ?? '-'}</dd>
            <dt>{t('shipment_field_origin_country')}</dt>
            <dd>
              {[shipment.originCity, shipment.originCountryCode]
                .filter(Boolean)
                .join(', ') || '-'}
            </dd>
            <dt>{t('shipment_field_destination_country')}</dt>
            <dd>
              {[shipment.destinationCity, shipment.destinationCountryCode]
                .filter(Boolean)
                .join(', ') || '-'}
            </dd>
            <dt>{t('shipment_detail_created_by')}</dt>
            <dd>{shipment.createdByName ?? '-'}</dd>
            <dt>{t('shipment_detail_created_at')}</dt>
            <dd>{formatDate(shipment.createdAt)}</dd>
          </dl>
        </div>

        {/* Cost breakdown - full-width row of the overview grid */}
        {canViewFinancials && shipmentId && (
          <section className="shipment-detail-cost">
            <h2 className="shipment-detail-cost__title">
              {t('shipment_tab_cost_breakdown')}
            </h2>
            <CostBreakdownTab shipmentId={shipmentId} />
          </section>
        )}

        {/* Document checklist - read-only in Phase 1 */}
        <div className="detail-card">
          <div className="detail-card__header">
            <h2 className="detail-card__title">
              {t('shipment_detail_documents')}
            </h2>
            {/* Grouped so the header's space-between keeps the title apart from the actions,
                rather than pushing the two buttons to opposite ends. */}
            <div className="detail-card__header-actions">
              {documentItems.length > 0 && (
                <button
                  className="btn btn-secondary"
                  onClick={handleDownloadAllDocuments}
                  disabled={downloadingAll}
                >
                  {downloadingAll
                    ? t('shipment_document_download_all_running')
                    : t('shipment_document_download_all')}
                </button>
              )}
              {isWriter && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.csv,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={documentUploading}
                  >
                    {documentUploading
                      ? t('shipment_document_uploading')
                      : t('shipment_detail_attach')}
                  </button>
                </>
              )}
            </div>
          </div>

          {documentsLoading ? (
            <p>{t('loading')}</p>
          ) : documentItems.length === 0 ? (
            <p className="detail-card__empty">
              {t('shipment_detail_no_documents')}
            </p>
          ) : (
            <ul className="shipment-document-list">
              {documentItems.map((doc) => (
                <li key={doc.id} className="shipment-document-list__item">
                  <div className="shipment-document-list__info">
                    <span className="shipment-document-list__name">
                      {doc.originalName}
                    </span>
                    <span className="shipment-document-list__meta">
                      {(doc.createdByName ?? t('unknown')) +
                        ' · ' +
                        formatDate(doc.createdAt) +
                        ' · ' +
                        formatFileSize(doc.size)}
                    </span>
                  </div>
                  <div className="shipment-document-list__actions">
                    {isDocumentPreviewable(doc.originalName, doc.mimeType) && (
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          setPreviewDoc({
                            id: doc.id,
                            originalName: doc.originalName,
                            mimeType: doc.mimeType,
                          })
                        }
                      >
                        {t('shipment_document_preview')}
                      </button>
                    )}
                    <button
                      className="btn btn-link"
                      onClick={() =>
                        handleDownloadDocument(doc.id, doc.originalName)
                      }
                    >
                      {t('shipment_document_download')}
                    </button>
                    {isWriter && (
                      <button
                        className="btn btn-link btn-link--danger"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        {t('delete')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Comments */}
        <div className="detail-card">
          <h2 className="detail-card__title">
            {t('shipment_detail_comments')}
          </h2>

          {isWriter && (
            <div className="shipment-comment-composer">
              <input
                className="input"
                type="text"
                placeholder={t('shipment_comment_placeholder')}
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handlePostComment();
                  }
                }}
              />
              <button
                className="btn btn-primary shipment-comment-composer__post"
                onClick={handlePostComment}
                disabled={commentPosting || commentBody.trim() === ''}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
                {t('shipment_comment_post')}
              </button>
            </div>
          )}

          {commentsLoading ? (
            <p>{t('loading')}</p>
          ) : commentItems.length === 0 ? (
            <p className="detail-card__empty">
              {t('shipment_detail_no_comments')}
            </p>
          ) : (
            <>
              <ul className="shipment-comment-list">
                {commentItems.slice(0, commentsVisible).map((comment) => (
                  <li key={comment.id} className="shipment-comment-list__item">
                    <span className="shipment-comment-list__author">
                      {comment.createdByName ?? t('unknown')}
                    </span>
                    <p className="shipment-comment-list__body">
                      {comment.body}
                    </p>
                    <span className="shipment-comment-list__time">
                      {formatDateTime(comment.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
              {commentsVisible < commentItems.length && (
                <button
                  className="btn btn-link shipment-comment-list__more"
                  onClick={() =>
                    setCommentsVisible((v) => v + COMMENTS_LOAD_STEP)
                  }
                >
                  {t('shipment_comment_load_more', {
                    count: Math.min(
                      COMMENTS_LOAD_STEP,
                      commentItems.length - commentsVisible,
                    ),
                  })}
                </button>
              )}
            </>
          )}
        </div>

        {/* Change history */}
        <div className="detail-card">
          <h2 className="detail-card__title">
            {t('shipment_detail_change_history')}
          </h2>
          {activityLoading ? (
            <p>{t('loading')}</p>
          ) : activityItems.length === 0 ? (
            <p className="detail-card__empty">
              {t('shipment_detail_no_activity')}
            </p>
          ) : (
            <ul className="shipment-activity-list">
              {activityItems.map((event) => (
                <li key={event.id} className="shipment-activity-list__item">
                  <span className="shipment-activity-list__time">
                    {formatDate(event.occurredAt)}
                  </span>
                  <span className="shipment-activity-list__actor">
                    {event.createdByName ?? t('unknown')}
                  </span>
                  <span className="shipment-activity-list__event">
                    {formatActivityEvent(
                      event.eventType,
                      event.eventParams ?? null,
                      t,
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {previewDoc && shipmentId && (
        <ShipmentDocumentPreviewModal
          shipmentId={shipmentId}
          docId={previewDoc.id}
          originalName={previewDoc.originalName}
          mimeType={previewDoc.mimeType}
          onClose={() => setPreviewDoc(null)}
          onDownload={() =>
            handleDownloadDocument(previewDoc.id, previewDoc.originalName)
          }
        />
      )}

      {showDeleteModal && (
        <ConfirmDeleteShipmentModal
          shipmentName={shipment.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default ShipmentTrackerDetailPage;
